<template>
  <div class="app-toast-host" aria-live="polite" aria-atomic="true">
    <div
      v-for="toast in toasts"
      :key="toast.id"
      class="app-toast"
      :class="`app-toast--${toast.type}`"
    >
      <div class="app-toast-icon">
        <i class="fa" :class="iconClass(toast.type)"></i>
      </div>
      <div class="app-toast-content">
        <div v-if="toast.title" class="app-toast-title">{{ toast.title }}</div>
        <div class="app-toast-message">{{ toast.message }}</div>
      </div>
      <button type="button" class="app-toast-close" aria-label="Fermer" @click="dismiss(toast.id)">
        <i class="fa fa-times"></i>
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { AppToastPayload, AppToastType } from '@/support/toastBus';

interface RenderedToast extends AppToastPayload {
  id: number;
  type: AppToastType;
}

export default defineComponent({
  name: 'AppToastHost',
  data() {
    return {
      toasts: [] as RenderedToast[],
      nextId: 1,
    };
  },
  mounted() {
    window.addEventListener('suite241:toast', this.handleToast as unknown as (event: Event) => void);
  },
  beforeUnmount() {
    window.removeEventListener('suite241:toast', this.handleToast as unknown as (event: Event) => void);
  },
  methods: {
    handleToast(event: CustomEvent<AppToastPayload>) {
      const payload = event.detail || {};
      if (!payload.message) return;

      const toast: RenderedToast = {
        id: this.nextId++,
        type: payload.type || 'info',
        title: payload.title,
        message: payload.message,
        duration: payload.duration ?? 4200,
      };

      this.toasts.push(toast);
      window.setTimeout(() => this.dismiss(toast.id), toast.duration);
    },
    dismiss(id: number) {
      this.toasts = this.toasts.filter((toast) => toast.id !== id);
    },
    iconClass(type: AppToastType) {
      return {
        success: 'fa-circle-check',
        error: 'fa-circle-exclamation',
        warning: 'fa-triangle-exclamation',
        info: 'fa-circle-info',
      }[type];
    },
  },
});
</script>

<style scoped>
.app-toast-host {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  max-width: min(420px, calc(100vw - 24px));
  position: fixed;
  right: 1rem;
  top: 1rem;
  z-index: 3000;
}

.app-toast {
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-left: 4px solid #0d6efd;
  border-radius: 8px;
  box-shadow: 0 16px 36px rgba(15, 23, 42, 0.18);
  display: flex;
  gap: 0.65rem;
  padding: 0.75rem 0.75rem 0.75rem 0.85rem;
}

.app-toast--success {
  border-left-color: #198754;
}

.app-toast--error {
  border-left-color: #dc3545;
}

.app-toast--warning {
  border-left-color: #ffc107;
}

.app-toast-icon {
  font-size: 1rem;
  line-height: 1.25;
  padding-top: 0.1rem;
}

.app-toast--success .app-toast-icon {
  color: #198754;
}

.app-toast--error .app-toast-icon {
  color: #dc3545;
}

.app-toast--warning .app-toast-icon {
  color: #b58100;
}

.app-toast--info .app-toast-icon {
  color: #0d6efd;
}

.app-toast-content {
  flex: 1 1 auto;
  min-width: 0;
}

.app-toast-title {
  font-size: 0.82rem;
  font-weight: 700;
  margin-bottom: 0.1rem;
}

.app-toast-message {
  font-size: 0.8rem;
  line-height: 1.35;
}

.app-toast-close {
  background: transparent;
  border: 0;
  color: #6c757d;
  padding: 0;
}
</style>
